import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Label } from "semantic-ui-react";

import { ISearch } from "../../components/Search";
import { ITableAppRender } from "../../components/TableApp";
import TemplateTable from "../../components/TemplateTable";
import { eStatusConsulta } from "../../interfaces";
import ConsultaRepository, { IConsultaListModel } from "../../Repositories/ConsultaRepository";
import errors from "../../Utils/ExtractErrors";
import ConsultDetails from "./ConsultDetails";

interface IState {
    loading: boolean;
    list: IConsultaListModel[];
}

const initialState: IState = {
    loading: true,
    list: []
}

export default function Consults() {
    const [state, setState] = useState<IState>(initialState);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    useEffect(() => { load(); }, []);

    async function load() {
        setState(state => ({ ...state, loading: true }));
        try {
            const response = await ConsultaRepository.List({
                search: refSearch.current.searchString,
                pageNumber: refPageNumber.current,
                recordsByPage: refRecordsByPage.current
            });

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setState({ loading: false, list: response.list });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }
            const error = errors.getAllErrors(err.response.data);
            if (error !== undefined) {
                toast(error, { position: "top-center", type: "error" });
            }
            setState(state => ({ ...state, loading: false }));
        }
    }

    function onSearch({ searchString, type }: ISearch) {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;
        load();
    }

    function onChangeRecords(value: number) {
        refRecordsByPage.current = value;
        load();
    }

    function onPageChange(pageNumber: string | number | undefined) {
        refPageNumber.current = pageNumber;
        load();
    }

    function handleTableActions(data: IConsultaListModel, index: number) {
        return ([
            <ConsultDetails idConsulta={data.id} />
        ]);
    }

    function setStatus(status: eStatusConsulta) {
        switch (status) {
            case eStatusConsulta.Analise:
                return <Label ribbon icon="search" color="yellow" content="ANL" />;
            case eStatusConsulta.Aprovado:
                return <Label ribbon icon="thumbs up" color="green" content="APV" />;
            case eStatusConsulta.Reprovado:
                return <Label ribbon icon="dont" color="black" content="RPV" />;
            default:
                return <Label ribbon icon="question" color="grey" content="???" />;
        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (consulta: IConsultaListModel) => setStatus(consulta.status?.id),
            width: 1
        },
        {
            header: "Data",
            content: (consulta: IConsultaListModel) => format(new Date(consulta.dataConsulta), "dd/MM/yyyy HH:mm"),
            width: 2
        },
        {
            header: "CPF",
            content: (consulta: IConsultaListModel) => consulta.cpf,
            width: 2
        },
        {
            header: "Nome",
            content: (consulta: IConsultaListModel) => consulta.nome,
            width: 3
        },
        {
            header: "Representante",
            content: (consulta: IConsultaListModel) => consulta.representante,
            width: 4
        },
    ];

    return (
        <TemplateTable
            title="Consultas"
            icon="search"
            searchPlaceHolder="CPF/Nome"
            onSearch={onSearch}
            filterType="simples"
            tableLoading={state.loading}
            tableList={state.list}
            tableRender={tableRender}
            tableActions={handleTableActions}
            tableOnChangeRecords={onChangeRecords}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalPages: refTotalPages.current,
                totalRecords: refTotalRecords.current,
                onPageChange: onPageChange
            }}
        />
    );
}
